import React, { useState, useEffect } from 'react';
import usePersistedTab from '../hooks/usePersistedTab';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  Casino,
  AttachMoney,
  SportsEsports,
  ShowChart,
  Timer,
  EmojiEvents,
  Refresh,
  PlayArrow,
  Stop,
  BugReport,
  CheckCircle,
  Cancel,
  Settings as SettingsIcon,
  ExpandMore,
  Save,
  RestartAlt
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import { formatNumber } from '../utils/formatters';
import GameCardsEditor from '../components/GameCardsEditor';

const MiniGames = () => {
  const [games, setGames] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selectedGame, setSelectedGame] = useState('lunar_lottery');
  const [testMode, setTestMode] = useState('win');
  const [testResult, setTestResult] = useState(null);
  const [stats, setStats] = useState({
    totalGames: 0,
    activeBoosts: 0,
    totalBoostsGiven: 0,
    averageBoostTime: 0
  });

  // Настройки ротации
  const [rotationSettings, setRotationSettings] = useState({
    rotationInterval: 24,
    autoRotation: true,
    forcedGame: null,
    currentDailyGame: 'lunar_lottery',
    lastRotation: null,
    nextRotation: null
  });
  const [rotationLoading, setRotationLoading] = useState(false);

  // Табы и конфигурация
  const [currentTab, setCurrentTab] = usePersistedTab('minigames', 0);
  const [gamesConfig, setGamesConfig] = useState({});
  const [configLoading, setConfigLoading] = useState(false);

  useEffect(() => {
    loadGames();
    loadRotationSettings();
    loadGamesConfig();
  }, [page, rowsPerPage]);

  const loadGames = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getGames({
        page: page + 1,
        limit: rowsPerPage
      });

      if (response.data.success) {
        // Маппинг данных API на ожидаемый формат компонента
        const mappedGames = (response.data.games || []).map(game => ({
          ...game,
          result: game.is_winner ? 'win' : 'lose',
          boost_percent: game.is_winner ? (game.reward_amount || 10) : 0,
          boost_duration: game.is_winner ? 5 : 0, // Фиксированная длительность буста
          status: game.completed_at ? 'completed' : 'in_progress',
          created_at: game.started_at || game.created_at
        }));

        setGames(mappedGames);
        setStats(response.data.stats || stats);
      }
    } catch (error) {
      console.error('Ошибка загрузки игр:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRotationSettings = async () => {
    try {
      const response = await adminAPI.getRotationSettings();
      if (response.data.success) {
        setRotationSettings(response.data.data || response.data.settings);
      }
    } catch (error) {
      console.error('Ошибка загрузки настроек ротации:', error);
    }
  };

  const loadGamesConfig = async () => {
    setConfigLoading(true);
    try {
      const response = await adminAPI.getMinigamesConfig();
      if (response.data.success) {
        setGamesConfig(response.data.data || {});
      }
    } catch (error) {
      console.error('Ошибка загрузки конфигурации игр:', error);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleUpdateGameConfig = async (gameType, config) => {
    setConfigLoading(true);
    try {
      const response = await adminAPI.updateMinigameConfig(gameType, config);
      if (response.data.success) {
        await loadGamesConfig();
        // Перезагружаем настройки в backend
        await adminAPI.reloadMinigamesSettings();
        alert(`✅ Конфигурация игры ${getGameName(gameType)} обновлена`);
      }
    } catch (error) {
      console.error('Ошибка обновления конфигурации:', error);
      alert(`❌ Ошибка обновления конфигурации: ${error.response?.data?.error || error.message}`);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleToggleGame = async (gameType, enabled) => {
    setConfigLoading(true);
    try {
      const response = await adminAPI.toggleMinigame(gameType, enabled);
      if (response.data.success) {
        await loadGamesConfig();
        alert(`✅ Игра ${getGameName(gameType)} ${enabled ? 'включена' : 'выключена'}`);
      }
    } catch (error) {
      console.error('Ошибка переключения игры:', error);
      alert(`❌ Ошибка переключения игры: ${error.response?.data?.error || error.message}`);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleReloadSettings = async () => {
    setConfigLoading(true);
    try {
      const response = await adminAPI.reloadMinigamesSettings();
      if (response.data.success) {
        alert('✅ Настройки мини-игр перезагружены из БД');
      }
    } catch (error) {
      console.error('Ошибка перезагрузки настроек:', error);
      alert(`❌ Ошибка перезагрузки настроек: ${error.response?.data?.error || error.message}`);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleUpdateRotationInterval = async (interval) => {
    setRotationLoading(true);
    try {
      const response = await adminAPI.updateRotationSettings({
        rotationInterval: interval
      });
      if (response.data.success) {
        await loadRotationSettings();
        alert(`✅ Интервал ротации обновлён: ${interval} часов`);
      }
    } catch (error) {
      console.error('Ошибка обновления интервала:', error);
      alert(`❌ Ошибка обновления интервала: ${error.response?.data?.error || error.message}`);
    } finally {
      setRotationLoading(false);
    }
  };

  const handleForceGame = async (gameId) => {
    setRotationLoading(true);
    try {
      const response = await adminAPI.forceGame(gameId || null);
      if (response.data.success) {
        await loadRotationSettings();
        const gameName = gameId ? getGameName(gameId) : null;
        alert(gameId
          ? `✅ Игра "${gameName}" установлена для всех пользователей`
          : '✅ Возврат к автоматической ротации игр');
      }
    } catch (error) {
      console.error('Ошибка форсирования игры:', error);
      alert(`❌ Ошибка форсирования игры: ${error.response?.data?.error || error.message}`);
    } finally {
      setRotationLoading(false);
    }
  };

  const handleRotateNow = async () => {
    setRotationLoading(true);
    try {
      const response = await adminAPI.rotateNow();
      if (response.data.success) {
        await loadRotationSettings();
        const newGameName = getGameName(response.data.newGame);
        alert(`✅ Игра сменена на: ${newGameName}`);
      }
    } catch (error) {
      console.error('Ошибка ротации:', error);
      alert(`❌ Ошибка ротации игры: ${error.response?.data?.error || error.message}`);
    } finally {
      setRotationLoading(false);
    }
  };

  // Тестовые данные для отображения
  const testGames = [
    {
      id: 1,
      user_id: 1,
      username: 'admin',
      game_type: 'lottery',
      boost_percent: 10,
      boost_duration: 5,
      result: 'win',
      status: 'completed',
      created_at: '2025-09-10 14:30:00',
      game_data: { numbers: [7, 14, 21] }
    },
    {
      id: 2,
      user_id: 2,
      username: 'player1',
      game_type: 'flip',
      boost_percent: 15,
      boost_duration: 3,
      result: 'lose',
      status: 'completed',
      created_at: '2025-09-10 14:25:30',
      game_data: { choice: 'heads', result: 'tails' }
    },
    {
      id: 3,
      user_id: 3,
      username: 'whale',
      game_type: 'dice',
      boost_percent: 20,
      boost_duration: 2,
      result: 'win',
      status: 'completed',
      created_at: '2025-09-10 14:20:15',
      game_data: { roll: 6 }
    },
    {
      id: 4,
      user_id: 1,
      username: 'admin',
      game_type: 'race',
      boost_percent: 25,
      boost_duration: 1,
      result: 'win',
      status: 'completed',
      created_at: '2025-09-10 14:15:45',
      game_data: { position: 1, time: '12.5s' }
    },
    {
      id: 5,
      user_id: 2,
      username: 'player1',
      game_type: 'mining',
      boost_percent: 30,
      boost_duration: 1,
      result: 'lose',
      status: 'completed',
      created_at: '2025-09-10 14:12:18',
      game_data: { blocks_mined: 3 }
    }
  ];

  const getGameIcon = (gameType) => {
    const icons = {
      lunar_lottery: '🎰',
      star_flip: '💰',
      cosmic_dice: '🎲',
      meteor_race: '🏃',
      asteroid_miner: '⛏️',
      // Старые ключи для совместимости
      lottery: '🎰',
      flip: '💰',
      dice: '🎲',
      race: '🏃',
      mining: '⛏️'
    };
    return icons[gameType] || '🎮';
  };

  const getGameName = (gameType) => {
    const names = {
      lunar_lottery: 'Лунная лотерея',
      star_flip: 'Звездная монетка',
      cosmic_dice: 'Космические кости',
      meteor_race: 'Гонка метеоров',
      asteroid_miner: 'Майнер астероидов',
      // Старые ключи для совместимости
      lottery: 'Лотерея',
      flip: 'Монетка',
      dice: 'Кости',
      race: 'Гонка',
      mining: 'Майнинг'
    };
    return names[gameType] || gameType;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const displayGames = games.length > 0 ? games : testGames;

  // Компонент для редактирования конфигурации игры
  const GameConfigEditor = ({ gameType }) => {
    const config = gamesConfig[gameType] || {};
    const [localConfig, setLocalConfig] = useState(config);

    useEffect(() => {
      setLocalConfig(config);
    }, [config]);

    const handleSave = () => {
      handleUpdateGameConfig(gameType, localConfig);
    };

    const handleFieldChange = (field, value) => {
      setLocalConfig(prev => ({ ...prev, [field]: value }));
    };

    return (
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Основные параметры */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>Основные параметры</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Название игры"
              value={localConfig.name || ''}
              onChange={(e) => handleFieldChange('name', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Описание"
              value={localConfig.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Бесплатных попыток"
              type="number"
              value={localConfig.free_attempts || 3}
              onChange={(e) => handleFieldChange('free_attempts', parseInt(e.target.value))}
              helperText="Количество бесплатных игр"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Начальная стоимость (монет)"
              type="number"
              value={localConfig.paid_game_cost || 10}
              onChange={(e) => handleFieldChange('paid_game_cost', parseFloat(e.target.value))}
              helperText="Стоимость 4-й игры"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Шаг увеличения стоимости"
              type="number"
              value={localConfig.cost_increment || 10}
              onChange={(e) => handleFieldChange('cost_increment', parseFloat(e.target.value))}
              helperText="На сколько растет цена каждой игры"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Макс. платных игр"
              type="number"
              value={localConfig.max_paid_games || 10}
              onChange={(e) => handleFieldChange('max_paid_games', parseInt(e.target.value))}
              helperText="Всего платных игр (после бесплатных)"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Кулдаун (сек)"
              type="number"
              value={localConfig.cooldown || 0}
              onChange={(e) => handleFieldChange('cooldown', parseFloat(e.target.value))}
              helperText="Время до следующей игры"
            />
          </Grid>

          {/* Шанс победы и длительность буста только для игр без карточек */}
          {(gameType === 'star_flip' || gameType === 'lunar_lottery') && (
            <>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Шанс победы (%)"
                  type="number"
                  inputProps={{ step: 1, min: 0, max: 100 }}
                  value={localConfig.win_chance ? localConfig.win_chance * 100 : 0}
                  onChange={(e) => handleFieldChange('win_chance', parseFloat(e.target.value) / 100)}
                  helperText="Процент шанса выигрыша"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Длительность буста (сек)"
                  type="number"
                  value={localConfig.boost_duration || 0}
                  onChange={(e) => handleFieldChange('boost_duration', parseFloat(e.target.value))}
                  helperText="Сколько длится буст при победе"
                />
              </Grid>
            </>
          )}

          {/* Специфичные параметры для lunar_lottery */}
          {gameType === 'lunar_lottery' && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>Множители за совпадения</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Множитель за 1 совпадение"
                  type="number"
                  value={localConfig.match_1_multiplier || 1}
                  onChange={(e) => handleFieldChange('match_1_multiplier', parseFloat(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Множитель за 2 совпадения"
                  type="number"
                  value={localConfig.match_2_multiplier || 2}
                  onChange={(e) => handleFieldChange('match_2_multiplier', parseFloat(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Множитель за 3 совпадения"
                  type="number"
                  value={localConfig.match_3_multiplier || 10}
                  onChange={(e) => handleFieldChange('match_3_multiplier', parseFloat(e.target.value))}
                />
              </Grid>
            </>
          )}

          {/* Редактор карточек для игр */}
          <Grid item xs={12}>
            <GameCardsEditor
              gameType={gameType}
              config={localConfig.config || localConfig[`${gameType}_config`]}
              onConfigChange={(newConfig) => handleFieldChange('config', newConfig)}
            />
          </Grid>

          {/* Кнопка сохранения */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={configLoading}
              sx={{ bgcolor: '#10b981', mt: 2 }}
            >
              Сохранить изменения
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Мини-игры
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<RestartAlt />}
            onClick={handleReloadSettings}
            disabled={configLoading}
            sx={{ bgcolor: '#f59e0b' }}
          >
            Перезагрузить настройки
          </Button>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={loadGames}
            sx={{ bgcolor: '#6366f1' }}
          >
            Обновить
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Мини-игры</strong> - это бусты, которые временно увеличивают доходность стейкинга.
          Игроки не делают ставки, а просто играют и получают бусты на 60-300 секунд.
        </Typography>
      </Alert>

      {/* Табы для переключения между разделами */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="История игр" />
          <Tab label="Настройки ротации" />
          <Tab label="⚙️ Конфигурация игр" />
        </Tabs>
      </Paper>

      {/* Таб 1: Настройки ротации */}
      {currentTab === 1 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            🔄 Настройки ротации игр
          </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                {rotationSettings.forcedGame ? 'Форсированная игра:' : 'Текущая игра дня:'}
              </Typography>
              <Chip
                label={getGameName(rotationSettings.forcedGame || rotationSettings.currentDailyGame) || 'Не установлена'}
                color={rotationSettings.forcedGame ? 'warning' : 'primary'}
                icon={<Typography>{getGameIcon(rotationSettings.forcedGame || rotationSettings.currentDailyGame)}</Typography>}
                sx={{ fontSize: '16px', p: 2 }}
              />
              {rotationSettings.forcedGame && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                  ⚠️ Авто-ротация отключена
                </Typography>
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Интервал ротации (часов):
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {[12, 24, 48].map((interval) => (
                  <Button
                    key={interval}
                    variant={rotationSettings.rotationInterval === interval ? 'contained' : 'outlined'}
                    onClick={() => handleUpdateRotationInterval(interval)}
                    disabled={rotationLoading}
                  >
                    {interval}ч
                  </Button>
                ))}
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Разброс кулдауна (процент ±):
              </Typography>
              <TextField
                type="number"
                value={rotationSettings.cooldownVariance || 15}
                onChange={(e) => {
                  const newSettings = { ...rotationSettings, cooldownVariance: parseFloat(e.target.value) };
                  setRotationSettings(newSettings);
                }}
                onBlur={() => {
                  adminAPI.updateRotationSettings({ cooldownVariance: rotationSettings.cooldownVariance });
                }}
                size="small"
                sx={{ width: 150 }}
                InputProps={{ endAdornment: '%' }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Рандомный разброс: кулдаун ±{rotationSettings.cooldownVariance}%
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Активное время (часы):
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  label="С"
                  type="time"
                  value={rotationSettings.activeTimeStart || '00:00'}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    const newSettings = { ...rotationSettings, activeTimeStart: newValue };
                    setRotationSettings(newSettings);
                    adminAPI.updateRotationSettings({ activeTimeStart: newValue });
                  }}
                  size="small"
                  sx={{ width: 150 }}
                  InputLabelProps={{ shrink: true }}
                />
                <Typography>до</Typography>
                <TextField
                  label="До"
                  type="time"
                  value={rotationSettings.activeTimeEnd || '23:59'}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    const newSettings = { ...rotationSettings, activeTimeEnd: newValue };
                    setRotationSettings(newSettings);
                    adminAPI.updateRotationSettings({ activeTimeEnd: newValue });
                  }}
                  size="small"
                  sx={{ width: 150 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Игры доступны только в указанное время суток
              </Typography>
            </Box>

            {rotationSettings.nextRotation && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Следующая ротация:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {new Date(rotationSettings.nextRotation).toLocaleString('ru-RU')}
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Форсировать игру для всех пользователей:
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={rotationSettings.forcedGame || 'auto'}
                  onChange={(e) => handleForceGame(e.target.value === 'auto' ? null : e.target.value)}
                  disabled={rotationLoading}
                >
                  <MenuItem value="auto">
                    🔄 Авто-ротация
                  </MenuItem>
                  <MenuItem value="lunar_lottery">🎰 Лунная лотерея</MenuItem>
                  <MenuItem value="star_flip">💰 Звездная монетка</MenuItem>
                  <MenuItem value="cosmic_dice">🎲 Космические кости</MenuItem>
                  <MenuItem value="meteor_race">🏃 Гонка метеоров</MenuItem>
                  <MenuItem value="asteroid_miner">⛏️ Майнер астероидов</MenuItem>
                </Select>
              </FormControl>
              {rotationSettings.forcedGame && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Форсированная игра отключает авто-ротацию для всех пользователей
                </Alert>
              )}
            </Box>

            <Box>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={handleRotateNow}
                disabled={rotationLoading || !!rotationSettings.forcedGame}
                fullWidth
                sx={{ bgcolor: '#10b981' }}
              >
                Сменить игру сейчас
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Принудительно переключить на следующую игру в ротации
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      )}

      {/* Таб 2: Конфигурация игр */}
      {currentTab === 2 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            ⚙️ Конфигурация мини-игр
          </Typography>

          {configLoading && <LinearProgress sx={{ mb: 2 }} />}

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Здесь вы можете настроить все параметры каждой мини-игры. Изменения сохраняются в БД и автоматически применяются.
            </Typography>
          </Box>

          {/* Accordion для каждой игры */}
          {['lunar_lottery', 'star_flip', 'cosmic_dice', 'meteor_race', 'asteroid_miner'].map((gameType) => (
            <Accordion key={gameType} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography fontSize={24}>{getGameIcon(gameType)}</Typography>
                  <Typography variant="h6">{getGameName(gameType)}</Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={gamesConfig[gameType]?.enabled !== false}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleToggleGame(gameType, e.target.checked);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                    label={gamesConfig[gameType]?.enabled !== false ? 'Включена' : 'Выключена'}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <GameConfigEditor gameType={gameType} />
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      )}

      {/* Таб 0: Статистика и история */}
      {currentTab === 0 && (
        <>
          {/* Статистика */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}>
            <CardContent sx={{ p: 2 }}>
              <Typography color="white" variant="body2" sx={{ fontSize: 12 }}>
                Всего игр сыграно
              </Typography>
              <Typography color="white" variant="h6" sx={{ fontWeight: 'bold', my: 0.5, fontSize: 18 }}>
                {formatNumber(stats.totalGames || displayGames.length)}
              </Typography>
              <Typography color="white" variant="body2" sx={{ fontSize: 11, opacity: 0.8 }}>
                За все время
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white'
          }}>
            <CardContent sx={{ p: 2 }}>
              <Typography color="white" variant="body2" sx={{ fontSize: 12 }}>
                Активных бустов
              </Typography>
              <Typography color="white" variant="h6" sx={{ fontWeight: 'bold', my: 0.5, fontSize: 18 }}>
                {formatNumber(stats.activeBoosts || 12)}
              </Typography>
              <Typography color="white" variant="body2" sx={{ fontSize: 11, opacity: 0.8 }}>
                Сейчас активно
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white'
          }}>
            <CardContent sx={{ p: 2 }}>
              <Typography color="white" variant="body2" sx={{ fontSize: 12 }}>
                Выдано бустов
              </Typography>
              <Typography color="white" variant="h6" sx={{ fontWeight: 'bold', my: 0.5, fontSize: 18 }}>
                {formatNumber(stats.totalBoostsGiven || 1876)}
              </Typography>
              <Typography color="white" variant="body2" sx={{ fontSize: 11, opacity: 0.8 }}>
                Всего выиграно
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: 'white'
          }}>
            <CardContent sx={{ p: 2 }}>
              <Typography color="white" variant="body2" sx={{ fontSize: 12 }}>
                Среднее время буста
              </Typography>
              <Typography color="white" variant="h5" sx={{ fontWeight: 'bold', my: 0.5 }}>
                {stats.averageBoostTime || 192} сек
              </Typography>
              <Typography color="white" variant="body2" sx={{ fontSize: 11, opacity: 0.8 }}>
                В среднем
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Таблица игр */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          История игр
        </Typography>
        
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Игрок</TableCell>
                <TableCell>Игра</TableCell>
                <TableCell>Результат</TableCell>
                <TableCell>Буст</TableCell>
                <TableCell>Длительность</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Время</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayGames.map((game) => (
                <TableRow key={game.id}>
                  <TableCell>{game.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {game.username}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {game.user_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontSize={20}>{getGameIcon(game.game_type)}</Typography>
                      <Typography>{getGameName(game.game_type)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={game.result === 'win' ? 'Выигрыш' : 'Проигрыш'}
                      color={game.result === 'win' ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {game.result === 'win' ? (
                      <Typography color="success.main" sx={{ fontWeight: 'bold' }}>
                        +{game.boost_percent}%
                      </Typography>
                    ) : (
                      <Typography color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {game.result === 'win' ? (
                      <Chip
                        icon={<Timer />}
                        label={`${game.boost_duration} сек`}
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Typography color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={game.status === 'completed' ? 'Завершено' : 'В процессе'}
                      color={game.status === 'completed' ? 'default' : 'warning'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(game.created_at).toLocaleString('ru-RU')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={100}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Строк на странице:"
        />
      </Paper>
        </>
      )}

    </Box>
  );
};

export default MiniGames;